import {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, NavLink} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import BuildingTitle from "../../common/buildingTitle";
import {LineGraph, PieChart} from "./buildingManagerLogic";
import {fetchManagerBuildingData} from '../../store/buildings/actions';
import {isDemoPartnerAccount} from '../../common/shared/statelessMethods';
import {hideLoader, showLoader} from '../../store/common/slice';
import {BuildingManagerProfileStyled} from './buildingManagerProfile.styled';
import ProfileStats from './profileStats'
import { constants } from '../../common/constants/constants';
import ArrowRight from '../../images/arrow-right.png';
import FloatingButton from './FloatingButton';
import {useAccessToken} from '../../common/hooks/useAccessToken';
import {isAdminSelector} from '../../store/auth/selectors';
import BllinkTime from '../../common/utils/time';
import ErrorsHandler from "../../common/errors/handleErrors";
import {NotificationManager} from "react-notifications";
import ContentLoader from "../../common/simple/ContentLoader";

export default function BuildingManagerProfile({history, match, isManagementCompany}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {buildingID, partnerID} = match.params;
    document.title = t('titles.dashboard');

    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector((state) => state.common.ltrEnabled);

    const managerData = useSelector(state => state.buildings.managerData);
    const [dashboardData, setDashboardData] = useState({});
    const [buildingData, setBuildingData] = useState({});
    const [monthAmounts, setMonthAmounts] = useState(null);

    const client = useAccessToken();

    const { month, year } = BllinkTime.getTimeNow();

    useEffect(async () => {
        if (client == null) return;
        try {
            const data = partnerID == null ? await client.managers.dashboard.getMonthAmounts(buildingID) :
                                             await client.partners.dashboard.getMonthAmounts(partnerID);
            setMonthAmounts(data.monthTotals.map(tt => ({ year: tt.year, month: tt.month, amount: tt.TotalInCreditCard ?? { value: 0 } })));
        } catch (e) {
            NotificationManager.error(e.message);
            setMonthAmounts({ monthAmounts: [] });
        }
    }, [client]);

    useEffect(async () => {
            if (!client) return;

            if (partnerID) {
                dispatch(showLoader());
                const partnerData = await getPartnerData();
                setDashboardData(partnerData.partnerStats);
                dispatch(hideLoader());
                return;
            }
            if (managerData != null) {
                setBuildingData(managerData);
                setDashboardData(managerData.stats);
            }
    }, [managerData, client]);

    async function getPartnerData() {
        if (isDemoPartnerAccount(partnerID)) {
            // load dynamically to exclude from common bundle
            const fileName = constants.getDemoPartnerProfileDataFileName(partnerID);
            const module = await import(`../../common/mocks/data/managers/${fileName}`);
            const partnerProfileData = module.default;
            return partnerProfileData.value;
        }
        try {
            const data = await client.partners.dashboard.getStats(partnerID);
            return data;
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
            return { partnerStats: {} };
        }
    }

    if (!dashboardData || !dashboardData.monthAmounts) {
        return <div className="building-manager-container" />
    }

    let connectBankAccount = null;

    const {paymentsMethods} = dashboardData;

    return (
        <>
            <BuildingManagerProfileStyled className={'p-0'}>
                {
                    buildingID ? (
                        <BuildingTitle
                            address={buildingData.address}
                            type="welcome"
                            sidebarPage
                        />
                    ) : (
                        <Fragment>
                            <div className={ltr ? 'back-link mt-2 ltr' : 'back-link mt-2' } data-testid='back-link'>
                                <Link to={`/${constants.pages.allBuildingsUrl}`}>
                                    <img alt='return_to_details' src={ArrowRight} />
                                    <span>{t('reports.go_back')}</span>
                                </Link>
                            </div>

                            <h1 className="page-title text-only centered mt-4 mb-4">
                                {t('reports.mc_dashboard_title', 
                                    {
                                        name: (<span className='partner-name'>{dashboardData.partner && dashboardData.partner.partnerName}</span>)
                                    }
                                )}
                            </h1>

                            <div className='partner-actions'>
                                <button className='bllink-button action-btn'>
                                    <NavLink to={`/${constants.pages.partnerDebtList}/${partnerID}`}>{t('reports.partner_debt_list')}</NavLink>
                                </button>

                                <button className='bllink-button action-btn'>
                                    <NavLink to={`/${constants.pages.partnerMonthlyReports}/${partnerID}/${month}/${year}`}>{t('reports.monthly_reports_at_partner_level')}</NavLink>
                                </button>
                            </div>
                        </Fragment>
                    )
                }

                <section>
                    <ProfileStats
                        dashboardData={dashboardData}
                        buildingData={buildingData}
                        isMcDashboard={isManagementCompany}
                    />
                </section>

                <section className='profile-charts'>
                    <div className="align-paye_chart">
                        <div className="paye_chart">
                            <h5>{t('buildings.payment_by_methods_title')}</h5>
                            <div className='canvas-pie'>
                                <PieChart paymentsMethods={paymentsMethods} />
                            </div>
                        </div>
                        {connectBankAccount}
                    </div>
                    <div className="align-line_chart">
                        <div className="line_chart">
                            <div style={{"position": "relative"}}>
                                {monthAmounts === null && (<ContentLoader width="100%" height={404} />)}
                                {monthAmounts !== null && (<LineGraph monthAmounts={monthAmounts} isAdmin={isAdmin} />)}
                            </div>
                        </div>
                    </div>
                </section>
            </BuildingManagerProfileStyled>
        </>
    );
}
